import { GetStaticProps } from 'next';
import Link from 'next/link';
import styled from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

const MOBILE = '(max-width: 992px)';

const UpskillsNewTeachersStudy = () => {
  return (
    <LandingPageLayout
      title="Research Study: Curipod Upskills New Teachers | Curipod"
      subTitle="A 2025 study found that new teachers using Curipod improved lesson quality from 2.6 to 4.3 out of 5, cut planning time by 30%, and gained confidence from day one."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>RESEARCH STUDY</HeroTag>
            <HeroTitle>
              Curipod Upskills New Teachers and Reduces Planning Time by 30%
            </HeroTitle>
            <HeroMeta>
              <MetaBadge>Pre-Service Teachers</MetaBadge>
              <MetaBadge>Lesson Planning</MetaBadge>
              <MetaBadge>2025 Study</MetaBadge>
            </HeroMeta>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <StatItem>
                  <StatNumber>
                    2.6<StatUnit>/5</StatUnit>
                  </StatNumber>
                  <StatLabel>Lesson Quality Before</StatLabel>
                </StatItem>
                <ArrowContainer>&rarr;</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    4.3<StatUnit>/5</StatUnit>
                  </StatNumber>
                  <StatLabel>Lesson Quality After</StatLabel>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    30<StatUnit>%</StatUnit>
                  </StatNumber>
                  <StatLabel>Less Planning Time</StatLabel>
                </StatItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* SUMMARY */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionText>
              A{' '}
              <a
                href="https://www.researchgate.net/publication/393447960_Empowering_Pre-Service_English_Teachers_Through_AI-Based_Lesson_Design_A_Case_Study_on_Curipod"
                target="_blank"
                rel="noopener noreferrer"
              >
                recent independent study
              </a>{' '}
              found that new teachers using Curipod boosted lesson quality (from 2.6 to
              4.3 on a 5-point scale), cut planning time by 30%, and felt more confident
              designing lessons. Curipod offers a scalable way to upskill new teachers,
              giving them the tools to design high-quality, engaging lessons from day one
              - with less stress and more creativity. Curipod acts as a planning partner,
              not a replacement, helping teachers focus on what matters most: students.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* INTRODUCTION */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Introduction</SectionTitle>
            <SectionText>
              Curipod is an AI-powered lesson design and interactive teaching platform. A
              2025 study by Murod Normuminov explored how Curipod can empower pre-service
              English teachers in lesson planning. A small but illustrative study of six
              teacher trainees in Uzbekistan&apos;s &ldquo;Teachnology&rdquo; program used
              Curipod to create and deliver 30-minute demo lessons. The study&apos;s
              findings offer insight into how AI tools like Curipod can enhance teacher
              preparation &ndash; a critical takeaway for K-12 curriculum directors and
              high school principals looking to support new educators.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* AI IMPROVES LESSON STRUCTURE */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>
              AI Improves Lesson Structure and Reduces Planning Stress
            </SectionTitle>
            <HighlightText>
              Average lesson structure ratings improved from 2.6 (before Curipod) to 4.3
              (after using Curipod), indicating a major enhancement in lesson quality.
            </HighlightText>
            <SectionText>
              According to the study, Curipod&apos;s AI-driven support &ldquo;enhanced the
              structural quality of lessons, reduced planning anxiety, and encouraged
              creative integration of multimodal elements&rdquo;. In other words, lessons
              became easier to follow and more engaging, with clearer sequencing and
              smoother transitions. Notably, the average lesson structure rating across
              participants rose from 2.6 to 4.3 on a 5-point scale after using Curipod.
              This significant jump, illustrated above, highlights how the AI tool helped
              novices organize content more effectively. Moreover, teachers reported that
              lesson planning time dropped by roughly <strong>30%</strong> when using
              Curipod, as the platform made it easier to generate warm-ups, vocabulary
              exercises, and interactive tasks. For school leaders, this efficiency means
              novice teachers can focus less on formatting and more on student learning.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* FOSTERING CONFIDENCE */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>
              Fostering Confidence and Creativity in New Teachers
            </SectionTitle>
            <SectionText>
              Curipod acted as a digital scaffold, helping trainees feel more confident
              and creative in their lesson planning. Trainees described a clear shift
              &ndash; from feeling stressed by planning logistics to feeling confident
              enough to focus on instruction and student engagement.
            </SectionText>

            <BlockQuote>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                Curipod gave me the skeleton I needed&mdash;I just had to fill in the
                flesh with my teaching style.
              </QuoteText>
              <QuoteAttrib>&mdash; Study Participant</QuoteAttrib>
            </BlockQuote>

            <BlockQuote style={{ marginTop: 24 }}>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                I used to stress about slide design and transitions. Curipod did that for
                me, so I could focus on the students.
              </QuoteText>
              <QuoteAttrib>&mdash; Study Participant</QuoteAttrib>
            </BlockQuote>

            <SectionText style={{ marginTop: 32 }}>
              These testimonials underscore that Curipod not only automates routine tasks
              (like slide formatting) but also{' '}
              <strong>sparks creative experimentation</strong>. Teachers leveraged
              Curipod&apos;s brainstorming features to develop rich lesson scenarios and
              adapted AI-suggested prompts to fit their classroom context. This balance of
              structure and freedom helps new teachers hit the ground running &ndash; a
              result school leaders consistently look for.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* AI AS TEACHING PARTNER */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>
              AI as a Teaching Partner &ndash; with Teachers in Control
            </SectionTitle>
            <SectionText>
              The study underscores that AI like Curipod supports teachers &ndash; it
              doesn&apos;t replace them. Curipod served as a{' '}
              <strong>
                &ldquo;planning scaffold, supporting pedagogical thinking without
                replacing the teacher&apos;s role&rdquo;
              </strong>
              . For school leaders evaluating edtech, this distinction matters: teachers
              stay in control, while AI handles the scaffolding.
            </SectionText>
            <SectionText>
              Teachers should retain editorial control, customizing AI-generated material
              to fit their students&apos; needs and local curriculum. The study recommends
              adding reflective tasks to teacher training so educators learn to question
              and adapt AI outputs &ndash; a key step toward digital literacy. By
              fostering this critical digital literacy, a district can harness
              Curipod&apos;s benefits while maintaining high-quality, contextually
              relevant instruction.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* IMPLICATIONS */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>
              Implications for Curriculum Directors and Principals
            </SectionTitle>
            <SectionText>
              For K&ndash;12 curriculum directors and high school principals, this study
              provides evidence-based reassurance for adopting Curipod as a
              lesson-planning tool. Novice teachers using Curipod develop stronger lesson
              designs with less stress &ndash; and build confidence more quickly. In
              practice, a first-year teacher with Curipod can deliver organized, engaging
              lessons from day one, potentially boosting both student outcomes and teacher
              retention. The 30% reduction in planning time frees teachers to focus on
              refining instruction or mentoring students.
            </SectionText>
            <SectionText>
              Principals could integrate Curipod into new-teacher induction programs or
              professional development workshops, framing it as a{' '}
              <strong>&ldquo;co-teacher&rdquo; or planning partner</strong>. Since Curipod
              encourages creativity (e.g. adding multimedia, interactive questions) while
              handling routine setup, it aligns with 21st-century teaching practices that
              value innovation and student engagement.
            </SectionText>
            <SectionText>
              Adopting Curipod signals a supportive, forward-thinking school culture
              &ndash; one that values innovation while lightening teachers&apos; load. As
              the study concludes, platforms like Curipod{' '}
              <strong>
                &ldquo;support novice teachers in internalizing components of effective
                lesson planning while freeing up mental bandwidth for instructional
                creativity.&rdquo;
              </strong>
            </SectionText>
            <SectionText>
              For a curriculum director aiming to improve instructional quality across the
              board, such AI-enhanced scaffolding could be a game-changer. Thoughtful
              implementation is key: provide training, encourage reflective use, and keep
              teacher expertise at the center. This balance turns AI from a buzzword into
              a practical asset that empowers teachers and enriches learning.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* SOURCE & CTA */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <CenteredSection>
            <SourceCard>
              <SourceLabel>SOURCE</SourceLabel>
              <SourceText>
                Normuminov, Murod. (2025). Empowering Pre-Service English Teachers Through
                AI-Based Lesson Design: A Case Study on Curipod.{' '}
                <em>Journal of Digital Sociohumanities</em>. 2. 142&ndash;149.
                10.25077/jds.2.2.142-149.2025.
              </SourceText>
              <a
                href="https://www.researchgate.net/publication/393447960_Empowering_Pre-Service_English_Teachers_Through_AI-Based_Lesson_Design_A_Case_Study_on_Curipod"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SourceLink>View on ResearchGate &rarr;</SourceLink>
              </a>
            </SourceCard>

            <GetStartedWrapper>
              <Link href="/signup" passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default UpskillsNewTeachersStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: ${theme.colors.paleBeige};
  width: 100%;
  overflow: hidden;
  position: relative;
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 52px;
  color: ${theme.colors.black};
  margin: 0 0 20px;
  line-height: 1.15;
  max-width: 800px;

  @media ${MOBILE} {
    font-size: 36px;
  }

  @media (max-width: 320px) {
    font-size: 28px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const StatBannerWrapper = styled.div`
  background: ${theme.colors.black};
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0;
  padding: 0 20px;

  @media ${MOBILE} {
    gap: 20px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: center;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 48px;

  @media ${MOBILE} {
    width: 100%;
    height: 1px;
    margin: 0;
  }
`;

const StatItem = styled.div<{ $highlight?: boolean }>`
  text-align: center;
  padding: 16px 24px;
  border-radius: 10px;
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  border: ${({ $highlight }) =>
    $highlight ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'};
`;

const StatNumber = styled.div<{ $highlight?: boolean }>`
  font-size: 56px;
  font-weight: bold;
  color: ${({ $highlight }) => ($highlight ? theme.colors.orange : theme.colors.white)};
  line-height: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;

  @media ${MOBILE} {
    font-size: 44px;
  }
`;

const StatUnit = styled.span`
  font-size: 24px;
  margin-top: 6px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin-top: 6px;
`;

const ArrowContainer = styled.div`
  font-size: 36px;
  color: ${theme.colors.white};
  font-weight: bold;

  @media ${MOBILE} {
    font-size: 24px;
  }
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 26px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  a {
    color: ${theme.colors.orange};
    text-decoration: underline;
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const HighlightText = styled.p`
  font-size: 20px;
  color: ${theme.colors.black};
  line-height: 1.6;
  font-weight: 600;
  font-style: italic;
  margin-bottom: 24px;
  padding: 20px 24px;
  background: rgba(249, 151, 70, 0.08);
  border-left: 4px solid ${theme.colors.orange};
  border-radius: 0 10px 10px 0;

  @media ${MOBILE} {
    font-size: 17px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── QUOTES ────────────────────────────────────────────────────────────────── */

const BlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 32px 0 0;
  background: rgba(249, 151, 70, 0.08);
  border-radius: 0 10px 10px 0;
`;

const QuoteMark = styled.span`
  display: block;
  font-size: 48px;
  color: ${theme.colors.orange};
  line-height: 0.8;
  margin-bottom: 8px;
`;

const QuoteText = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.5;
  margin: 0 0 12px;

  @media ${MOBILE} {
    font-size: 18px;
  }
`;

const QuoteAttrib = styled.cite`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

/* ─── SOURCE & CTA ──────────────────────────────────────────────────────────── */

const SourceCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 12px;
  padding: 24px 32px;
  box-shadow: ${theme.effects.defaultShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 20px;
  }
`;

const SourceLabel = styled.div`
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  margin-bottom: 8px;
`;

const SourceText = styled.p`
  font-size: 15px;
  color: ${theme.colors.text.dark};
  line-height: 1.6;
  margin: 0 0 8px;
`;

const SourceLink = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orange};
  &:hover {
    text-decoration: underline;
  }
`;

const GetStartedWrapper = styled.div`
  margin-top: 8px;
`;
